import { loggingService } from "../../services/centralized-logging/loggingService";
import { sanitizeData, sanitizeUrl } from "./sanitizeUtils";
import { getUserInfo, getUsernameFromSessionStorage } from "./userUtils";
import { generateRequestId } from "./requestUtils";

const APPLICATION_NAME = "Pago-En-Linea-Generico";
const LOGGING_ENABLED =
  process.env.REACT_APP_CENTRALIZED_LOGGING_API_URL &&
  process.env.REACT_APP_CENTRALIZED_LOGGING_API_KEY;

function parseBody(body) {
  if (body == null || typeof body !== "string") return body;
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
}

function resolveUserName(headers, body) {
  const cookieUsername = getUsernameFromSessionStorage();
  if (cookieUsername) return cookieUsername;
  return getUserInfo({
    headers,
    data: parseBody(body),
  }).userId;
}

async function logHttpRequest({ requestId, method, url, headers, body }) {
  if (!LOGGING_ENABLED) return;
  try {
    const safeUrl = sanitizeUrl(url);
    const userName = resolveUserName(headers, body);
    await loggingService.logEvent({
      eventType: "Information",
      description: `HTTP REQUEST - ${method} ${safeUrl}`,
      userName,
      application: APPLICATION_NAME,
      environment: process.env.NODE_ENV || "development",
      additionalData: {
        type: "HTTP_REQUEST",
        requestId,
        method,
        url: safeUrl,
        headers: sanitizeData(headers),
        data: sanitizeData(parseBody(body)),
        timestamp: new Date().toISOString(),
        userAgent: getUserInfo({}).userAgent,
      },
    });
  } catch (error) {
    console.error("Error al registrar request log:", error);
  }
}

async function logHttpResponse({
  requestId,
  method,
  url,
  headers,
  body,
  response,
  responseData,
}) {
  if (!LOGGING_ENABLED) return;
  try {
    const safeUrl = sanitizeUrl(url);
    const userName = resolveUserName(headers, body);
    await loggingService.logEvent({
      eventType: "Information",
      description: `HTTP RESPONSE - ${response.status} ${method} ${safeUrl}`,
      userName,
      application: APPLICATION_NAME,
      environment: process.env.NODE_ENV || "development",
      additionalData: {
        type: "HTTP_RESPONSE",
        requestId,
        method,
        url: safeUrl,
        statusCode: String(response.status),
        statusText: response.statusText,
        headers: sanitizeData(Object.fromEntries(response.headers.entries())),
        data: sanitizeData(responseData),
        timestamp: new Date().toISOString(),
        success: response.ok,
      },
    });
  } catch (error) {
    console.error("Error al registrar response log:", error);
  }
}

async function logHttpError({ requestId, method, url, headers, body, error }) {
  if (!LOGGING_ENABLED) return;
  try {
    const safeUrl = sanitizeUrl(url);
    const userName = resolveUserName(headers, body);
    await loggingService.logEvent({
      eventType: "Error",
      description: `HTTP ERROR - ${method} ${safeUrl} - ${error.message || "Unknown error"}`,
      userName,
      application: APPLICATION_NAME,
      environment: process.env.NODE_ENV || "development",
      additionalData: {
        type: "HTTP_ERROR",
        requestId,
        method,
        url: safeUrl,
        errorMessage: error.message,
        errorCode: error.name,
        timestamp: new Date().toISOString(),
        success: false,
      },
    });
  } catch (err) {
    console.error("Error al registrar error log:", err);
  }
}

/**
 * fetch con logging centralizado para llamadas a REACT_APP_API_URL_TREE_SCALE.
 * API compatible con fetch nativo.
 */
export async function loggedFetch(url, options = {}) {
  const requestId = generateRequestId();
  const method = (options.method || "GET").toUpperCase();
  const headers = options.headers || {};
  const body = options.body;
  const timeoutMs = Number(process.env.REACT_APP_TIME_OUT) || 60000;

  if (process.env.REACT_APP_DEBUG_MODE === "true") {
    console.log({ method, url, headers, body });
  }

  logHttpRequest({ requestId, method, url, headers, body }).catch((e) =>
    console.error("Error en logging de request:", e)
  );

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    let responseData;
    try {
      responseData = await response.clone().json();
    } catch {
      try {
        responseData = await response.clone().text();
      } catch {
        responseData = null;
      }
    }

    if (process.env.REACT_APP_DEBUG_MODE === "true") {
      console.log(response);
    }

    logHttpResponse({
      requestId,
      method,
      url,
      headers,
      body,
      response,
      responseData,
    }).catch((e) => console.error("Error en logging de response:", e));

    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    logHttpError({ requestId, method, url, headers, body, error }).catch((e) =>
      console.error("Error en logging de error:", e)
    );
    throw error;
  }
}
